import z from 'zod'
import { cn, objectEntries } from '@/helpers/utils'
import {
	FILTER_PREVIOUS_LABEL,
	OVERVIEW_CARD_CLASSNAME
} from '@/pages/luxe-submissions-mm-data-overview'
import { useTranslation } from '@/translations/i18n'
import type {
	MMDataOverviewDatePreset,
	MMDataOverviewRecentActivity
} from '../types'

const RecentActivityKeys = z.enum([
	'quotesGenerated',
	'secondReviewRequested',
	'reInspectionNeeded',
	'listingsSubmitted'
])

type RecentActivityKeys = z.infer<typeof RecentActivityKeys>

const ACTIVITY_I18N_KEYS = {
	quotesGenerated: 'luxe_submissions:recent_activity_labels.quotes_generated',
	secondReviewRequested:
		'luxe_submissions:recent_activity_labels.second_review_requested',
	reInspectionNeeded:
		'luxe_submissions:recent_activity_labels.re_inspection_needed',
	listingsSubmitted:
		'luxe_submissions:recent_activity_labels.listing_submitted'
} as const satisfies Record<RecentActivityKeys, string>

const NEUTRAL_COLOR = 'border-neutral-300 text-neutral-700'

const ACTIVITY_CHANGE_COLORS: Record<
	RecentActivityKeys,
	{ positive: string; negative: string }
> = {
	quotesGenerated: {
		positive: 'border-success-300 text-success-700',
		negative: NEUTRAL_COLOR
	},
	secondReviewRequested: {
		positive: 'border-warning-300 text-warning-700',
		negative: NEUTRAL_COLOR
	},
	reInspectionNeeded: {
		positive: 'border-warning-300 text-warning-700',
		negative: NEUTRAL_COLOR
	},
	listingsSubmitted: {
		positive: 'border-success-300 text-success-700',
		negative: NEUTRAL_COLOR
	}
}

interface MMOverviewRecentActivityProps {
	data: MMDataOverviewRecentActivity
	filter: MMDataOverviewDatePreset | 'custom_range'
}

function MMOverviewRecentActivity({
	data,
	filter
}: MMOverviewRecentActivityProps) {
	const { t } = useTranslation()

	return (
		<div className={cn(OVERVIEW_CARD_CLASSNAME, 'col-span-2 gap-6 px-8')}>
			<span className='font-semibold text-grey-1000 text-lg'>
				{t('luxe_submissions:recent_activity')}
			</span>

			<div className='grid grid-cols-4 gap-4'>
				{objectEntries(data).map(([key, metric]) => {
					const change = metric.current - metric.previous
					const colors = ACTIVITY_CHANGE_COLORS[key]
					const changeColor =
						change > 0 ? colors.positive : colors.negative

					return (
						<div
							className='flex flex-col gap-2 rounded-lg bg-neutral-50 p-4'
							key={key}
						>
							<span className='font-semibold text-grey-1000 text-xl-fixed'>
								{metric.current}
							</span>
							<span className='font-medium text-base text-neutral-700'>
								{t(ACTIVITY_I18N_KEYS[key])}
							</span>
							<div>
								<span
									className={cn(
										'inline-block rounded-full border px-2 py-0.5 text-xs',
										changeColor
									)}
								>
									{change > 0 ? '+' : ''}
									{change}{' '}
									{` vs ${FILTER_PREVIOUS_LABEL[filter]}`}
								</span>
							</div>
						</div>
					)
				})}
			</div>
		</div>
	)
}

export { MMOverviewRecentActivity }
